import type { AppMode } from "@madamuh/domain";
import type { RelationType } from "@madamuh/domain";
import type { MemberRole } from "@madamuh/domain";
import { GENERIC_TENANT_LABELS, type TenantLabels } from "./tenantLabels";

const APP_MODE_LABELS: Record<AppMode, string> = {
  family: "ホーム",
  operator: "運営モード"
};

const RELATION_TYPE_LABELS: Record<RelationType, string> = {
  parent: "保護者",
  guardian: "ご家族",
  self: "本人"
};

const STATUS_LABELS: Record<string, string> = {
  active: "有効",
  inactive: "無効",
  pendingStart: "開始待ち",
  draft: "下書き",
  published: "公開中",
  hidden: "非表示",
  scheduled: "公開予定",
  uploadIssued: "アップロード準備済み",
  uploaded: "アップロード済み",
  failed: "失敗",
  revoked: "失効",
  expired: "期限切れ",
  consumed: "使用済み",
  submitted: "申請中",
  approved: "承認済み",
  rejected: "却下",
  cancelled: "休講",
  completed: "完了",
  event: "イベント",
  siblingMergedChronological: "兄弟姉妹まとめ表示",
  operator: "運営モード",
  parent: "保護者",
  guardian: "ご家族",
  self: "本人",
  tbd: "未定",
  assigned: "割当済み",
  paused: "停止中",
  ended: "終了",
  archived: "アーカイブ済み",
  deleted: "削除済み",
  image: "画像",
  pdf: "PDF",
  video: "動画",
  available: "利用可能",
  notAllowed: "利用不可",
  noVisibleStudents: "表示対象なし",
  pending: "処理中",
  critical: "緊急",
  high: "高",
  normal: "通常",
  low: "低",
  confirmed: "確定",
  private: "非公開",
  public: "公開",
  suspended: "停止中",
  regular: "通常",
  trial: "体験",
  shortTerm: "短期",
  manual: "個別追加",
  auto: "自動",
  generator: "自動生成",
  replacement: "代替動画",
  eventSupport: "イベント補助",
  rescheduled: "振替",
  overrideApplied: "上書き調整済み",
  edited: "手動調整済み",
  occurrenceChanged: "予定変更",
  schedulePublished: "予定公開",
  weekly: "毎週",
  monthlyWeeks1To4: "第1〜第4週",
  monthlyWeeks1And3: "第1・第3週",
  monthlyWeeks2And4: "第2・第4週",
  monthlyWeekOfStartDate: "開始日の週のみ",
  irregular: "不定期"
};

export function describeTenantMemberRole(role: MemberRole, labels: TenantLabels): string {
  switch (role) {
    case "parent":
      return "保護者";
    case "student":
      return `${labels.student}本人`;
    case "teacher":
      return labels.teacher;
    case "admin":
      return "管理者";
    default:
      return role;
  }
}

export function describeMemberRole(role: MemberRole, labels: TenantLabels = GENERIC_TENANT_LABELS): string {
  return describeTenantMemberRole(role, labels);
}

export function describeMemberRoles(roles: readonly MemberRole[], labels: TenantLabels = GENERIC_TENANT_LABELS): string {
  return roles.map((role) => describeMemberRole(role, labels)).join(" / ");
}

export function describeAppMode(mode: AppMode | null | undefined): string {
  if (!mode) {
    return "未設定";
  }

  return APP_MODE_LABELS[mode] ?? mode;
}

export function describeRelationType(value: RelationType | null | undefined): string {
  if (!value) {
    return "未設定";
  }

  return RELATION_TYPE_LABELS[value] ?? value;
}

export function describeTenantEntityType(value: string | null | undefined, labels: TenantLabels): string {
  if (!value) {
    return "未設定";
  }

  switch (value) {
    case "lesson":
      return labels.lesson;
    case "occurrence":
      return `${labels.lesson}予定`;
    case "student":
      return `${labels.student}ごと`;
    case "family":
      return "家族単位";
    default:
      return describeStatus(value);
  }
}

export function describeTenantCommunicationCategory(value: string | null | undefined, labels: TenantLabels): string {
  if (!value) {
    return "未設定";
  }

  switch (value) {
    case "lessonSupport":
      return `${labels.lesson}補助`;
    case "occurrenceSupport":
      return `${labels.lesson}予定補助`;
    case "eventSupport":
      return "イベント補助";
    default:
      return describeStatus(value);
  }
}

export function describeTenantStatus(value: string | null | undefined, labels: TenantLabels): string {
  if (value === "lesson" || value === "occurrence" || value === "student" || value === "family") {
    return describeTenantEntityType(value, labels);
  }

  if (value === "lessonSupport" || value === "occurrenceSupport" || value === "eventSupport") {
    return describeTenantCommunicationCategory(value, labels);
  }

  return describeStatus(value);
}

export function describeNotificationTargetType(value: string | null | undefined, labels: TenantLabels): string | null {
  if (!value) {
    return null;
  }

  switch (value) {
    case "memberCommunication":
      return "連絡事項";
    case "student":
      return labels.student;
    case "lesson":
      return labels.lesson;
    case "occurrence":
      return `${labels.lesson}予定`;
    case "event":
      return "イベント";
    case "video":
      return "動画";
    case "scheduleMonth":
      return "予定";
    case "guardianLinkRequest":
      return "保護者連携申請";
    case "guardianInvite":
      return "保護者招待";
    default:
      return null;
  }
}

export function describeStatus(value: string | null | undefined): string {
  if (!value) {
    return "未設定";
  }

  return STATUS_LABELS[value] ?? value;
}
